import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { localizeApiError } from "@/lib/apiErrors";
import { useI18n } from "@/context/I18nContext";
import { formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { ShieldCheck, Crown, Search } from "lucide-react";

export default function Admin() {
  const { t } = useI18n();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState("");

  const load = () => {
    setLoading(true);
    api.adminUsers().then((r) => setUsers(r.data)).catch(() => toast.error(t("toast.usersLoadError"))).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const grant = async () => {
    if (!email.trim()) { toast.error(t("toast.enterEmail")); return; }
    setSubmitting(true);
    try {
      await api.adminSetPremium(email.trim(), true);
      toast.success(t("toast.premiumActivated", { email: email.trim() }));
      setEmail(""); load();
    } catch (e) {
      toast.error(localizeApiError(e, t, "toast.activateError"));
    } finally { setSubmitting(false); }
  };

  const toggle = async (u, val) => {
    try {
      await api.adminSetPremium(u.email, val);
      setUsers((x) => x.map((it) => it.email === u.email ? { ...it, is_premium: val } : it));
    } catch { toast.error(t("toast.updateFailed")); }
  };

  const filtered = users.filter((u) => u.email.toLowerCase().includes(search.toLowerCase()));

  return (
    <div data-testid="admin-page">
      <div className="flex items-center gap-3 mb-6">
        <span className="h-10 w-10 rounded-lg bg-cyan-400/10 border border-cyan-400/25 flex items-center justify-center"><ShieldCheck className="text-cyan-300" style={{ width: 20, height: 20 }} /></span>
        <div>
          <h1 className="text-2xl md:text-3xl font-display font-semibold tracking-tight">{t("admin.title")}</h1>
          <p className="text-muted-foreground text-sm">{t("admin.subtitle")}</p>
        </div>
      </div>

      <div className="card-base p-5 mb-6">
        <h3 className="font-display font-semibold mb-3 flex items-center gap-2"><Crown className="text-cyan-300" style={{ width: 18, height: 18 }} /> {t("admin.activateByEmail")}</h3>
        <div className="flex flex-col sm:flex-row gap-3">
          <Input data-testid="admin-email-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("admin.emailPlaceholder")} className="flex-1 bg-white/5 border-white/10" />
          <Button data-testid="admin-grant-premium-submit" onClick={grant} disabled={submitting} className="bg-cyan-400 text-slate-900 hover:bg-cyan-300 font-semibold">
            {submitting ? t("admin.activating") : t("admin.activatePremium")}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-2">{t("admin.mustSignIn")}</p>
      </div>

      <div className="card-base overflow-hidden">
        <div className="p-4 border-b border-white/10">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" style={{ width: 16, height: 16 }} />
            <Input data-testid="admin-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("admin.searchUsers")} className="pl-9 bg-white/5 border-white/10" />
          </div>
        </div>
        {loading ? (
          <div className="p-4 space-y-2">{[0,1,2].map((i) => <Skeleton key={i} className="h-12 bg-white/5" />)}</div>
        ) : (
          <div className="divide-y divide-white/8">
            {filtered.map((u) => (
              <div key={u.user_id} data-testid="admin-user-row" className="flex items-center justify-between px-4 py-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate flex items-center gap-2">
                    {u.name || u.email}
                    {u.is_admin && <span className="text-xs rounded-full px-2 py-0.5 bg-cyan-400/10 text-cyan-200 border border-cyan-400/25">{t("admin.adminTag")}</span>}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">{u.email} · {t("admin.joined")} {formatDate(u.created_at)}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`text-xs ${u.is_premium ? "text-cyan-300" : "text-muted-foreground"}`}>{u.is_premium ? t("common.premium") : t("common.free")}</span>
                  <Switch data-testid="admin-premium-toggle" checked={!!u.is_premium} onCheckedChange={(v) => toggle(u, v)} />
                </div>
              </div>
            ))}
            {filtered.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground">{t("admin.noUsers")}</div>}
          </div>
        )}
      </div>
    </div>
  );
}
